import { formatSEK } from '@budgie/shared';
import { ChevronDown } from 'lucide-react';
import { Fragment, type MouseEvent } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SortableHead } from '@/components/ui/sortable-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { sortItems, useTableSort } from '@/lib/useTableSort';
import { cn } from '@/lib/utils';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { LoanHistory } from './LoanHistory';

type LoanItem = {
  id: string;
  name: string;
  totalAmount: number | null;
  interestRate: number | null;
  nextPaymentOverride: number | null;
  isActive: boolean;
  lastAmount: number | null;
  lastDate: string | null;
  lastAccountName: string | null;
  totalPayments: number;
  totalPaid: number;
  prediction: {
    amount: number;
    confidence: 'low' | 'medium' | 'high';
    dataPoints: number;
    trend: 'stable' | 'increasing' | 'decreasing';
  } | null;
};

type Props = {
  loans: LoanItem[];
  selectedLoanId: string | null;
  onSelectLoan: (loanId: string | null) => void;
  onCreate: () => void;
  onEdit: (
    loan: Pick<LoanItem, 'id' | 'name' | 'totalAmount' | 'interestRate' | 'nextPaymentOverride'>,
  ) => void;
};

const CONFIDENCE_LABELS = {
  low: 'Låg säkerhet',
  medium: 'Medel',
  high: 'Hög säkerhet',
} as const;

const TREND_ICONS = {
  stable: '→',
  increasing: '↑',
  decreasing: '↓',
} as const;

function formatInterestRate(rate: number) {
  return `${(rate / 100).toFixed(2)}%`;
}

function predictionHint(loan: LoanItem) {
  if (!loan.prediction) return null;
  return loan.nextPaymentOverride != null
    ? '✎ Manuell'
    : `${TREND_ICONS[loan.prediction.trend]} ${CONFIDENCE_LABELS[loan.prediction.confidence]}`;
}

export function LoansList({ loans, selectedLoanId, onSelectLoan, onCreate, onEdit }: Props) {
  const { confirm } = useConfirm();
  const { sort, toggle } = useTableSort<string>();
  const utils = trpc.useUtils();

  const deleteMutation = trpc.loan.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.loan.list.invalidate(), utils.loan.history.invalidate()]);
      toast.success('Lånet togs bort');
      onSelectLoan(null);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = async (loan: LoanItem) => {
    const ok = await confirm({
      title: `Ta bort "${loan.name}"?`,
      message:
        'Historik och kopplade regler tas bort från lånet. Själva transaktionerna påverkas inte.',
      confirmLabel: 'Ta bort',
      destructive: true,
    });

    if (!ok) return;

    deleteMutation.mutate({ id: loan.id });
  };

  const toggleSelected = (loan: LoanItem) => {
    onSelectLoan(selectedLoanId === loan.id ? null : loan.id);
  };

  const editClick = (loan: LoanItem) => (e: MouseEvent) => {
    e.stopPropagation();
    onEdit({
      id: loan.id,
      name: loan.name,
      totalAmount: loan.totalAmount,
      interestRate: loan.interestRate,
      nextPaymentOverride: loan.nextPaymentOverride,
    });
  };

  const deleteClick = (loan: LoanItem) => (e: MouseEvent) => {
    e.stopPropagation();
    void handleDelete(loan);
  };

  const sortedLoans = sortItems(loans, sort, {
    name: (l) => l.name,
    account: (l) => l.lastAccountName ?? '',
    totalAmount: (l) => l.totalAmount ?? 0,
    interestRate: (l) => l.interestRate ?? 0,
    lastAmount: (l) => l.lastAmount ?? 0,
    prediction: (l) => l.prediction?.amount ?? 0,
    totalPaid: (l) => l.totalPaid,
  });

  const totalLastAmount = sortedLoans.reduce((sum, l) => sum + (l.lastAmount ?? 0), 0);
  const totalPrediction = sortedLoans.reduce((sum, l) => sum + (l.prediction?.amount ?? 0), 0);

  if (loans.length === 0) {
    return (
      <EmptyState
        icon="🏦"
        title="Inga lån"
        description="Skapa ditt första lån för att börja följa amorteringar och räntor."
        action={{
          label: 'Skapa lån',
          onClick: onCreate,
        }}
      />
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card">
      <header className="flex items-baseline justify-between gap-3 px-4 pt-4 pb-3 sm:px-5">
        <h2 className="text-[15px] font-semibold">{loans.length} lån</h2>
        <p className="text-sm text-muted-foreground tabular-nums">
          Prognos{' '}
          <span className="font-semibold text-foreground">{formatSEK(totalPrediction)}</span>
        </p>
      </header>

      {/* Mobile: row list, tap a row for history and actions */}
      <ul className="divide-y divide-border border-t border-border md:hidden">
        {sortedLoans.map((loan) => {
          const isSelected = selectedLoanId === loan.id;
          const meta = [
            loan.totalAmount != null ? `Lån ${formatSEK(loan.totalAmount)}` : null,
            loan.interestRate != null ? `Ränta ${formatInterestRate(loan.interestRate)}` : null,
            `Betalt ${formatSEK(loan.totalPaid)} (${loan.totalPayments} bet.)`,
          ].filter(Boolean);

          return (
            <li key={loan.id}>
              <button
                type="button"
                className="flex min-h-14 w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset"
                aria-expanded={isSelected}
                onClick={() => toggleSelected(loan)}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="break-words font-medium">{loan.name}</span>
                    {!loan.isActive && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        Inaktivt
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {loan.lastAmount != null ? (
                      <>
                        Senast {formatSEK(loan.lastAmount)}
                        {loan.lastDate ? ` · ${loan.lastDate}` : ''}
                        {loan.lastAccountName ? ` · ${loan.lastAccountName}` : ''}
                      </>
                    ) : (
                      'Ingen betalning ännu'
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{meta.join(' · ')}</div>
                </div>
                <div className="flex shrink-0 items-start gap-1.5">
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">
                      {loan.prediction ? formatSEK(loan.prediction.amount) : '—'}
                    </div>
                    {loan.prediction && (
                      <div className="text-xs text-muted-foreground">{predictionHint(loan)}</div>
                    )}
                  </div>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'mt-0.5 size-4 text-muted-foreground transition-transform',
                      isSelected && 'rotate-180',
                    )}
                  />
                </div>
              </button>
              {isSelected && (
                <div className="space-y-3 bg-muted/40 px-4 pt-1 pb-4">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 flex-1"
                      onClick={editClick(loan)}
                    >
                      Redigera
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 flex-1 text-destructive hover:text-destructive"
                      disabled={deleteMutation.isPending}
                      onClick={deleteClick(loan)}
                    >
                      Ta bort
                    </Button>
                  </div>
                  <LoanHistory loanId={loan.id} />
                </div>
              )}
            </li>
          );
        })}
        <li className="flex items-center justify-between gap-3 bg-muted/40 px-4 py-3 text-sm font-semibold">
          <span>Totalt</span>
          <span className="text-right tabular-nums">
            <span className="block">{formatSEK(totalPrediction)}</span>
            <span className="block text-xs font-normal text-muted-foreground">
              Senast {formatSEK(totalLastAmount)}
            </span>
          </span>
        </li>
      </ul>

      {/* Desktop: table */}
      <div className="hidden border-t border-border md:block [&_td:first-child]:pl-5 [&_td:last-child]:pr-5 [&_th:first-child]:pl-5 [&_th:last-child]:pr-5">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableHead sortKey="name" sort={sort} onToggle={toggle}>
                Namn
              </SortableHead>
              <SortableHead sortKey="account" sort={sort} onToggle={toggle}>
                Dras från
              </SortableHead>
              <SortableHead
                sortKey="totalAmount"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Totalt
              </SortableHead>
              <SortableHead
                sortKey="interestRate"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Ränta
              </SortableHead>
              <SortableHead
                sortKey="lastAmount"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Senast
              </SortableHead>
              <SortableHead
                sortKey="prediction"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Prognos
              </SortableHead>
              <SortableHead
                sortKey="totalPaid"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Betalt
              </SortableHead>
              <TableHead className="text-right">
                <span className="sr-only">Åtgärder</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLoans.map((loan) => {
              const isSelected = selectedLoanId === loan.id;

              return (
                <Fragment key={loan.id}>
                  <TableRow
                    className="cursor-pointer"
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={() => toggleSelected(loan)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{loan.name}</span>
                        {!loan.isActive && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            Inaktivt
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {loan.lastAccountName ?? '—'}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {loan.totalAmount != null ? (
                        formatSEK(loan.totalAmount)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {loan.interestRate != null ? (
                        formatInterestRate(loan.interestRate)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {loan.lastAmount != null ? (
                        <div>
                          <div className="text-sm tabular-nums">{formatSEK(loan.lastAmount)}</div>
                          {loan.lastDate && (
                            <div className="text-xs text-muted-foreground tabular-nums">
                              {loan.lastDate}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {loan.prediction ? (
                        <div>
                          <div className="text-sm font-medium tabular-nums">
                            {formatSEK(loan.prediction.amount)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {predictionHint(loan)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <div className="text-sm tabular-nums">{formatSEK(loan.totalPaid)}</div>
                        <div className="text-xs text-muted-foreground">
                          {loan.totalPayments} bet.
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={editClick(loan)}
                        >
                          Redigera
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                          disabled={deleteMutation.isPending}
                          onClick={deleteClick(loan)}
                        >
                          Ta bort
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {isSelected && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={8} className="bg-muted/40 p-4 whitespace-normal">
                        <LoanHistory loanId={loan.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            <TableRow className="bg-muted/40 font-semibold hover:bg-muted/40">
              <TableCell>Totalt</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell className="text-right text-sm tabular-nums">
                {formatSEK(totalLastAmount)}
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums">
                {formatSEK(totalPrediction)}
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
